import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ErrorZone } from '../enums/error-zone';
import { ErrorService } from '../services/error.service';
import { parseGuidFromUrlToken } from '../utils/route-param-guid';

/**
 * Creates a guard that blocks routes whose `paramName` route parameter is not a well-formed
 * GUID entity ID, redirecting to `fallbackUrl` and showing a generic error toast.
 *
 * @param paramName Name of the route parameter holding the ID, e.g. `'inviteCode'`.
 * @param fallbackUrl URL to redirect to when the ID is missing or invalid, or a function of the
 * route snapshot computing one — e.g. to redirect back into a parent's still-valid context
 * rather than a fixed URL.
 * @param errorZone Zone determining the generic error toast content.
 */
export function guidParamGuard(
    paramName: string,
    fallbackUrl: string | ((route: ActivatedRouteSnapshot) => string),
    errorZone: ErrorZone
): CanActivateFn {
    return (route) => {
        if (parseGuidFromUrlToken(route.paramMap.get(paramName)) != null) {
            return true;
        }

        inject(ErrorService).generalToast(errorZone);

        const resolvedUrl =
            typeof fallbackUrl === 'function'
                ? fallbackUrl(route)
                : fallbackUrl;

        return inject(Router).parseUrl(resolvedUrl);
    };
}
